import React, { useEffect, useState } from "react";
import { Box, Typography, Grid, Stack, IconButton, Tooltip } from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import EditIcon from "@mui/icons-material/Edit";
import ApiService from "../../services/api-service";
import { Images } from "../../assets/common";
import { getUserId } from "../../constants/function";
import { useHistory } from "react-router-dom";
import AddPopup from "../../components/AddPopup";

export default function MyPlaylistsTab() {
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(false);
  const userId = getUserId();
  const [pageNo, setPageNo] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const history = useHistory();
  const [editPlaylist, setEditPlaylist] = useState(false);
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(null);

  // Handler for marking playlist private/public
  const handleTogglePrivate = async (pl, index) => {
    try {
      setLoading(true);
      await ApiService.ASSET_PRIVACY({
        assetId: pl._id,
        type: "playlist",
        private: !pl.private,
      });
      // Update state locally
      const updated = [...playlists];
      updated[index].private = !pl.private;
      setPlaylists(updated);
    } catch (e) {}
    setLoading(false);
  };

  // Handler for changing cover (open AddPopup)
  const handleChangeCover = (pl, index) => {
    setSelectedPlaylist(pl);
    setSelectedIndex(index);
    setEditPlaylist(true);
  };

  // Fetch playlists
  const fetchPlaylists = async (page = 1, append = false) => {
    setLoading(true);
    try {
      console.log('[SKWEB PLAYLIST] Fetching playlists for userId:', userId, 'page:', page);
      const res = await ApiService.PLAYLIST(
        {
          id: userId,
          pageNo: page,
          pageSize: 50, // Increased from 10 to 50
        },
        true
      );
      console.log('[SKWEB PLAYLIST] API response:', res);
      const newPlaylists = res.result || [];
      console.log('[SKWEB PLAYLIST] Playlists count:', newPlaylists.length);
      console.log('[SKWEB PLAYLIST] Playlists:', newPlaylists.map(p => ({ id: p._id, title: p.title })));

      if (append) {
        setPlaylists(prev => [...prev, ...newPlaylists]);
      } else {
        setPlaylists(newPlaylists);
      }

      // Check if there are more playlists to load
      setHasMore(newPlaylists.length === 50);
    } catch (e) {
      console.error('[SKWEB PLAYLIST] Error fetching playlists:', e);
      setPlaylists([]);
      setHasMore(false);
    }
    setLoading(false);
  };

  // Load more playlists
  const loadMore = () => {
    const nextPage = pageNo + 1;
    setPageNo(nextPage);
    fetchPlaylists(nextPage, true);
  };

  useEffect(() => {
    if (userId) {
      fetchPlaylists(1, false);
    }
    // eslint-disable-next-line
  }, [userId]);

  return (
    <Box sx={{ color: "var(--text-primary, #fff)", mt: 4 }}>
      <Typography variant="h5" sx={{ mb: 3 }}>My Playlists</Typography>
      {loading ? (
        <Typography color="var(--text-primary, #fff)">Loading...</Typography>
      ) : playlists.length === 0 ? (
        <Typography color="var(--text-primary, #fff)">No playlists found.</Typography>
      ) : (
        <Grid container spacing={4}>
          {playlists.map((pl, index) => (
            <Grid item key={pl._id || pl.id} xs={12} sm={6} md={4} lg={2}>
              <Box
                sx={{
                  background: "var(--background-color, #181818)",
                  borderRadius: "16px",
                  boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.15)",
                  overflow: "hidden",
                  cursor: "pointer",
                  transition: "box-shadow 0.3s",
                  "&:hover": { boxShadow: "0 12px 40px 0 rgba(31,38,135,0.25)" },
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  p: 0,
                  position: "relative",
                }}
                onClick={() => history.push(`/app/Music/${pl._id}`)}
              >
                {/* Action buttons overlay */}
                <Box sx={{ position: "absolute", top: 8, right: 8, zIndex: 2, display: "flex", gap: 1 }}>
                  <Tooltip title={pl.private ? "Make Public" : "Make Private"}>
                    <IconButton
                      size="small"
                      sx={{ bgcolor: "var(--accent-color, #222)", color: "var(--text-primary, #fff)", "&:hover": { bgcolor: "var(--background-color, #333)" } }}
                      onClick={e => { e.stopPropagation(); handleTogglePrivate(pl, index); }}
                    >
                      {pl.private ? <LockIcon fontSize="small" /> : <LockOpenIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Change Cover">
                    <IconButton
                      size="small"
                      sx={{ bgcolor: "var(--accent-color, #222)", color: "var(--text-primary, #fff)", "&:hover": { bgcolor: "var(--background-color, #333)" } }}
                      onClick={e => { e.stopPropagation(); handleChangeCover(pl, index); }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                {/* Main square image */}
                <Box
                  sx={{
                    width: "100%",
                    aspectRatio: "1 / 1",
                    background: "var(--accent-color, #222)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                  }}
                >
                  <img
                    src={
                      (pl.GIF && pl.GIF.filePath)
                        ? pl.GIF.filePath
                        : (pl.thumbnailImg && pl.thumbnailImg.filePath)
                          ? pl.thumbnailImg.filePath
                          : Images.defaultPlaylist
                    }
                    alt={pl.title || "Studio"}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                  {pl.private && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 12,
                        left: 12, // <-- Move to left corner
                        bgcolor: "rgba(0,0,0,0.6)",
                        px: 1.5,
                        py: 0.5,
                        borderRadius: "12px",
                        color: "var(--text-primary, #fff)",
                        fontSize: 12,
                        fontWeight: 600,
                        letterSpacing: 1,
                      }}
                    >
                      Private
                    </Box>
                  )}
                </Box>
                {/* Info section */}
                <Box sx={{ width: "100%", p: 2, background: "var(--background-color, #181818)" }}>
                  <Typography variant="h6" fontWeight={700} sx={{ color: "var(--text-primary, #fff)", mb: 0.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {pl.title || pl.name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "var(--primary-color, #8fff8f)", fontWeight: 600 }}>
                    {(Array.isArray(pl.music) ? pl.music.length : (pl.songsCount || pl.count || 0))} Songs
                  </Typography>
                  <Stack direction="row" spacing={2} sx={{ mt: 1, alignItems: "center" }}>
                    <Typography variant="caption" sx={{ color: "var(--text-secondary, #aaa)" }}>
                      {pl.platform}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "var(--text-secondary, #aaa)" }}>
                      {pl.listens ?? 0} listens
                    </Typography>
                    {pl.topTipper && (
                      <Typography variant="caption" sx={{ color: "var(--primary-color, #ffd700)" }}>
                        ⭐ {pl.topTipper}
                      </Typography>
                    )}
                  </Stack>
                  <Typography variant="caption" sx={{ color: "var(--text-secondary, #888)", mt: 1, display: "block" }}>
                    {pl.createdDate ? new Date(pl.createdDate).toLocaleDateString() : ""}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Load More Button */}
      {!loading && hasMore && playlists.length > 0 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <Button
            variant="contained"
            onClick={loadMore}
            sx={{
              bgcolor: "var(--primary-color, #ffe066)",
              color: "#222",
              fontWeight: 600,
              px: 4,
              py: 1.5,
              "&:hover": {
                bgcolor: "var(--primary-color-dark, #ffd700)",
              },
            }}
          >
            Load More Playlists
          </Button>
        </Box>
      )}

      {/* Change Cover Modal using AddPopup */}
      {editPlaylist ? (
        <AddPopup
          selectedIndex={selectedIndex}
          id={selectedPlaylist?._id}
          api="playlist"
          image={
            selectedPlaylist?.GIF?.filePath
              ? selectedPlaylist.GIF.filePath
              : selectedPlaylist?.thumbnailImg?.filePath
              ? selectedPlaylist.thumbnailImg.filePath
              : ""
          }
          title="Edit Playlist"
          isExplicit={selectedPlaylist?.isExplicit}
          open={editPlaylist}
          handleClose={(e, close, url, explicit) => {
            setEditPlaylist(false);
            setSelectedPlaylist(null);
            setSelectedIndex(null);
            if (!close) fetchPlaylists();
          }}
        />
      ) : null}
    </Box>
  );
}